import type { GroupPhoto } from '@lbc/shared';

import type { WrappedMonthlyComparison, WrappedStats } from './wrapped-model';
import { GroupWrappedSection } from './wrapped-group-section';
import { formatDecimal } from './wrapped-display-text';
import { WrappedHeader } from './wrapped-primitives';

export interface WrappedMembershipOption {
  id: string;
  groupId: string;
  groupName: string | null;
  displayName: string;
}

export function MonthlyWrappedSection({
  comparison,
  groupStats,
  groupName,
  memberships,
  selectedGroupId,
  hasGroupPicker,
  onSelectGroup,
  onBack,
  highlights,
}: {
  comparison: WrappedMonthlyComparison;
  groupStats: WrappedStats;
  groupName: string;
  memberships: Array<WrappedMembershipOption>;
  selectedGroupId: string | null;
  hasGroupPicker: boolean;
  onSelectGroup: (groupId: string) => void;
  onBack: () => void;
  highlights: GroupPhoto[];
}) {
  return (
    <main className="wrapped-page">
      <WrappedHeader onBack={onBack} />
      <section className="wrapped-monthly" aria-labelledby="wrapped-monthly-title">
        <div className="wrapped-section-heading">
          <p className="step-label">Persönlicher Monatsrückblick</p>
          <h1 id="wrapped-monthly-title">{comparison.monthLabel}</h1>
          <p>Deine Zahlen im Vergleich zu {comparison.previousMonthLabel}.</p>
        </div>
        <div className="wrapped-monthly-stat-group">
          <h2>Menge &amp; Trinktage</h2>
          <div className="wrapped-stat-grid" aria-label="Menge und Trinktage">
            <MonthlyWrappedStat
              label="Getränke"
              current={comparison.current.totalDrinks}
              previous={comparison.previous.totalDrinks}
              unit="Getränk"
            />
            <MonthlyWrappedStat
              label="Trinktage"
              current={comparison.current.drinkingDays}
              previous={comparison.previous.drinkingDays}
              unit="Trinktag"
            />
            <MonthlyWrappedStat
              label="Getränke pro Trinktag"
              current={comparison.current.averagePerDrinkingDay}
              previous={comparison.previous.averagePerDrinkingDay}
              unit="Getränk pro Trinktag"
              decimal
            />
          </div>
        </div>
        <div className="wrapped-monthly-stat-group">
          <h2>Rhythmus &amp; Pausen</h2>
          <div className="wrapped-stat-grid" aria-label="Rhythmus und Pausen">
            <MonthlyWrappedStat
              label="Längste Serie"
              current={comparison.current.longestStreak}
              previous={comparison.previous.longestStreak}
              unit="Tag"
            />
            <MonthlyWrappedStat
              label="Längste Pause"
              current={comparison.current.longestBreak}
              previous={comparison.previous.longestBreak}
              unit="Tag"
            />
            <MonthlyWrappedStat
              label="Tage ohne Eintrag"
              current={comparison.current.noDrinkDays}
              previous={comparison.previous.noDrinkDays}
              unit="Tag"
            />
          </div>
        </div>
        <div className="wrapped-monthly-stat-group">
          <h2>Getränkeauswahl</h2>
          <div className="wrapped-stat-grid" aria-label="Getränkeauswahl">
            <WrappedMonthlyTextStat
              label="Lieblingstyp"
              value={comparison.current.favoriteCategory ?? '—'}
              hint="Häufigster Getränketyp in diesem Monat"
            />
          </div>
        </div>
        <div className="wrapped-monthly-quote">
          <strong>{comparison.quote.speaker}</strong>
          <p>„{comparison.quote.text}“</p>
        </div>
      </section>

      <section className="wrapped-group-section" aria-labelledby="wrapped-monthly-group-title">
        {hasGroupPicker ? (
          <label className="wrapped-group-select">
            <span>Gruppe auswählen</span>
            <select
              value={selectedGroupId ?? ''}
              onChange={(event) => onSelectGroup(event.target.value)}
            >
              {memberships.map((membership) => (
                <option value={membership.groupId} key={membership.id}>
                  {membership.groupName ?? 'Ohne Gruppennamen'} · {membership.displayName}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="wrapped-section-heading">
          <div>
            <h2 id="wrapped-monthly-group-title">Gruppenauswertung</h2>
            <p>
              {groupName} im {comparison.monthLabel}.
            </p>
          </div>
        </div>
        <GroupWrappedSection stats={groupStats} groupName={groupName} highlights={highlights} />
      </section>
    </main>
  );
}

function MonthlyWrappedStat({
  label,
  current,
  previous,
  unit,
  decimal = false,
}: {
  label: string;
  current: number;
  previous: number;
  unit: string;
  decimal?: boolean;
}) {
  const delta = current - previous;
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'same';
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
  const deltaText =
    delta === 0
      ? 'Gleich wie im Vormonat'
      : `${decimal ? formatDecimal(Math.abs(delta)) : Math.abs(delta)} ${unit}${Math.abs(delta) === 1 ? '' : 'e'} ${delta > 0 ? 'mehr' : 'weniger'} als im Vormonat`;
  return (
    <article className="wrapped-monthly-stat">
      <strong>{decimal ? formatDecimal(current) : current}</strong>
      <span>{label}</span>
      <div className={`wrapped-monthly-stat__change wrapped-monthly-stat__change--${direction}`}>
        <b aria-hidden="true">{arrow}</b>
        <small>{deltaText}</small>
      </div>
    </article>
  );
}

function WrappedMonthlyTextStat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <article className="wrapped-monthly-stat">
      <strong>{value}</strong>
      <span>{label}</span>
      <small>{hint}</small>
    </article>
  );
}
